import { Injectable } from '@angular/core';
import { ClothingApiService } from '../../api/clothing-api.service';
import { firstValueFrom, Subject } from 'rxjs';
import { PersonApiService } from '../../api/person-api.service';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetClothingOutput } from '../../api/clothing-api.types';
import { GetPersonOutput, UpdatePersonInput } from '../../api/person-api.types';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class ScannerService {
  errorSubject = new Subject<string>();
  constructor(
    private clothingApiService: ClothingApiService,
    private personApiService: PersonApiService,
    private router: Router,
    private messageService: NzMessageService,
  ) {}

  private handleScanError(message: string) {
    // Play error beep
    new Audio('assets/error-beep.mp3').play();
    // Show warning message
    this.messageService.warning(message);
    // Emit error through subject
    this.errorSubject.next(message);
  }

  async massScanLaundryWithdrawal(result: string) {
    const scannedClothing = await firstValueFrom(
      this.clothingApiService.getAllClothing({ inventarnummer: result }),
    );
    if (scannedClothing.length !== 1) {
      return this.handleScanError(
        'Gescannte ID konnte nicht eindeutig zugewiesen werden.',
      );
    }

    const clothing = scannedClothing[0];
    if (clothing.status === 2) {
      this.handleScanError('Kleidungsstück als beschädigt markiert.');
      return this.router.navigate(['clothing', clothing.id]);
    }
    await this.clothingApiService.withdrawClothing(clothing.id);
    return this.messageService.success('Kleidung erfolgreich zurückgenommen.');
  }

  async massScanWithdrawal(result: string) {
    const scannedClothing = await firstValueFrom(
      this.clothingApiService.getAllClothing({ inventarnummer: result }),
    );
    if (scannedClothing.length !== 1) {
      return this.handleScanError(
        'Gescannte ID konnte nicht eindeutig zugewiesen werden.',
      );
    }
    const clothing = scannedClothing[0];

    await this.clothingApiService.withdrawClothing(clothing.id);
    await this.clothingApiService.launderClothing(clothing.id);
    return this.messageService.success(
      'Kleidung erfolgreich zurückgenommen und zum Waschen markiert.',
    );
  }

  async scanPerson(result: string) {
    console.log(result);
    try {
      await this.router.navigate(['/persons', result.slice(2)]);
    } catch {
      this.handleScanError(
        'Gescannte ID konnte nicht eindeutig zugewiesen werden.',
      );
    }
  }

  async scanClothing(result: string, personId: number) {
    const scannedClothing = await firstValueFrom(
      this.clothingApiService.getAllClothing({ inventarnummer: result }),
    );
    if (scannedClothing.length !== 1) {
      return this.handleScanError(
        'Gescannte ID konnte nicht eindeutig zugewiesen werden.',
      );
    }

    const clothing = scannedClothing[0];
    const hasBeenScanned = await this.checkIfAlreadyHasBeenScanned(clothing.id);
    if (hasBeenScanned) {
      this.handleScanError(
        'Kleidungsstück wurde in der letzten Minute bereits gescanned.',
      );
      return this.router.navigate(['clothing', clothing.id]);
    }

    if (clothing.status === 1) {
      await this.clothingApiService.withdrawClothing(clothing.id);
      await this.clothingApiService.launderClothing(clothing.id);

      const message =
        clothing.personId !== personId
          ? 'Achtung, zurückgenommenes und zum Waschen markiertes Kleidungsstück gehört nicht zum Azubi!'
          : 'Kleidungsstück erfolgreich zurückgenommen und zum Waschen markiert.';

      return this.messageService.success(message);
    }

    if (clothing.status === 4) {
      await this.clothingApiService.assignClothing({
        kleidungsstuecke: [clothing.id],
        person_id: personId,
      });

      await this.setInitialSizeAfterScan(clothing, personId);

      return this.messageService.success(
        'Kleidungsstück wurde erfolgreich zugewiesen.',
      );
    }

    return this.handleScanError(
      `Kleidungsstück ist bereits als "` +
        clothing.status_text +
        '" markiert. Bitte manuell bearbeiten',
    );
  }

  async setInitialSizeAfterScan(clothing: GetClothingOutput, personId: number) {
    try {
      const person = await firstValueFrom(
        this.personApiService.getPerson(personId),
      );
      const kleidergroessen = await this.clothingApiService.getClothingTypes();
      const size = clothing.kleidergroesse;
      const type = clothing.kleidungsart;

      const temp = kleidergroessen.find((kleidung) => kleidung.codenr === type);
      if (!temp) return;

      const clothingType = temp.text.toLowerCase();

      const sizeMapping: Record<string, keyof GetPersonOutput> = {
        hemd: 'groesse_hemd',
        hose: 'groesse_hose',
        jacke: 'groesse_jacke',
        kittel: 'groesse_kittel',
        schuh: 'groesse_schuhe',
        stiefel: 'groesse_schuhe',
      };

      let input: Partial<UpdatePersonInput> = {};

      Object.keys(sizeMapping).forEach((key) => {
        const attribute = sizeMapping[key];
        if (clothingType.includes(key) && person[attribute] === null) {
          input = { [sizeMapping[key]]: size };
        }
      });

      if (Object.keys(input).length > 0) {
        await this.personApiService.updatePerson(personId, person.typ, input);
      }
    } catch (error) {
      console.error('Initiale Größe konnte nicht zugewiesen werden:', error);
    }
  }

  async checkIfAlreadyHasBeenScanned(id: number) {
    const history = await this.clothingApiService.getClothingHistory(id);
    if (history.length > 0) {
      const lastEvent = history[history.length - 1];
      const date = DateTime.fromISO(lastEvent.datum);

      return date.plus({ minutes: 1 }) > DateTime.now();
    } else return false;
  }
}
